/**
 * CreditScoreSheet - Full credit score tracking bottom sheet
 * Shows empty state or full dashboard with history, goal, and tips
 */
import { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, TrendingUp, Lock } from "lucide-react";
import { Button } from "../ui/button";
import customToast from "../../utils/customToast";
import { usePurple } from "../../utils/purple";
import { usePlan } from "../../utils/plan"; // plan_03

const CreditScoreSheet = ({ isOpen, onClose, creditScore = {}, saveUserData, theme = 'pink' }) => {
  const isPink = theme === 'pink';
  const isWarm = theme === 'warm';
  // Purple (admin-only): lavender instead of hot pink and multicolour tiles.
  const purple = usePurple();
  // plan_03: Money plan - lilac gauge, black buttons, pastel bands.
  const plan = usePlan();

  const [showLogDialog, setShowLogDialog] = useState(false);
  const [scoreInput, setScoreInput] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [showGoalEdit, setShowGoalEdit] = useState(false);
  const [goalInput, setGoalInput] = useState('');
  const [showLearnView, setShowLearnView] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);

  // Lock the page behind the sheet while it's open (iOS-safe: pin the body, restore scroll on close)
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    return () => {
      Object.assign(body.style, prev);
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  const entries = creditScore?.entries || [];
  const goal = creditScore?.goal || 800;
  const hasEntries = entries.length > 0;

  // Sort entries chronologically
  const sorted = [...entries].sort((a, b) => a.month_key.localeCompare(b.month_key));
  const latestEntry = sorted[sorted.length - 1];
  const latestScore = latestEntry?.score || 0;
  const previousEntry = sorted.length > 1 ? sorted[sorted.length - 2] : null;
  const change = previousEntry ? latestScore - previousEntry.score : null;
  const firstEntry = sorted[0];
  const totalChange = sorted.length > 1 ? latestScore - firstEntry.score : null;

  const getPrevMonthName = () => {
    if (!previousEntry) return '';
    const [, m] = previousEntry.month_key.split('-').map(Number);
    return ['January','February','March','April','May','June','July','August','September','October','November','December'][m - 1] || '';
  };

  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const hasCurrentMonth = entries.some(e => e.month_key === currentMonthKey);

  const getRating = (score) => {
    if (plan) {
      if (score >= 760) return { label: 'Excellent', color: '#2F8756' };
      if (score >= 725) return { label: 'Very Good', color: '#2F8756' };
      if (score >= 660) return { label: 'Good', color: '#3C6DB5' };
      if (score >= 560) return { label: 'Fair', color: '#8A7A3C' };
      return { label: 'Poor', color: '#C04B7C' };
    }
    if (score >= 760) return { label: 'Excellent', color: '#047857' };
    if (score >= 725) return { label: 'Very Good', color: '#10b981' };
    if (score >= 660) return { label: 'Good', color: '#22c55e' };
    if (score >= 560) return { label: 'Fair', color: '#f59e0b' };
    return { label: 'Poor', color: '#ef4444' };
  };

  const rating = getRating(latestScore);

  // Primary button/bar colour — app standard: theme-600 for pink, theme-500 for blue/warm
  // Flat soft palette: one colour family per theme, taken from the gauge arc
  // btnBg = light (buttons, selected pill), bar = mid (progress bars), accent = dark (button text, %, Edit, chart line)
  const btnBg  = plan ? '#ECE6FA' : purple ? '#F1E9FB' : isPink ? '#EDE6F8' : isWarm ? '#F3EBE2' : '#E6E7FC';
  const bar    = plan ? '#B9A6F0' : purple ? '#AC8DF2' : isPink ? '#C8B6F0' : isWarm ? '#C9A583' : '#A5A8F5';

  const accent = plan ? '#7A5DD3' : purple ? '#5E35A8' : isPink ? '#6E55A8' : isWarm ? '#7C5C3A' : '#4338ca';
  const softBtn = plan ? { background: '#1F1E24', color: '#FFFFFF' } : { background: btnBg, color: accent };

  // Gauge - CSS semicircle with proper gradient fill
  const renderGauge = (score, empty = false) => {
    const scorePercent = empty ? 0 : Math.min(1, Math.max(0, (score - 300) / 600));
    const fillDeg = scorePercent * 180;
    
    const c1 = plan ? '#DDD3F6' : purple ? '#D9C8F6' : isPink ? '#F7A6C2' : isWarm ? '#B07D50' : '#4361EE';
    const c2 = plan ? '#B9A6F0' : purple ? '#8B5CE0' : isPink ? '#C8B6F0' : isWarm ? '#7C5C3A' : '#6366f1';
    
    // The visible semicircle is the TOP half (from 270deg/left through 0deg/top to 90deg/right)
    // conic-gradient angles: 0deg=top, 90deg=right, 180deg=bottom, 270deg=left
    const gradientBg = fillDeg > 0
      ? `conic-gradient(from 270deg, ${c1} 0deg, ${c2} ${fillDeg}deg, transparent ${fillDeg}deg, transparent 180deg, transparent 360deg)`
      : 'transparent';
    const glowBg = fillDeg > 0
      ? `conic-gradient(from 270deg, ${c1}40 0deg, ${c2}40 ${fillDeg}deg, transparent ${fillDeg}deg, transparent 180deg, transparent 360deg)`
      : 'transparent';
    
    const ringMask = 'radial-gradient(circle at center, transparent 72px, black 73px, black 99px, transparent 100px)';
    const glowMask = 'radial-gradient(circle at center, transparent 66px, black 67px, black 99px, transparent 100px)';
    
    return (
      <div className="relative flex justify-center" style={{ height: 125 }}>
        {/* Semicircle container - only shows top half */}
        <div style={{ width: 200, height: 100, position: 'relative', overflow: 'hidden' }}>
          {/* Gray background ring */}
          <div style={{
            width: 200, height: 200, borderRadius: '50%',
            position: 'absolute', top: 0, left: 0,
            background: 'conic-gradient(from 270deg, #e5e7eb 0deg, #e5e7eb 180deg, transparent 180deg)',
            WebkitMask: ringMask, mask: ringMask,
          }} />
          {/* Glow behind colored arc */}
          {!empty && fillDeg > 0 && (
            <div style={{
              width: 200, height: 200, borderRadius: '50%',
              position: 'absolute', top: 0, left: 0,
              background: glowBg,
              WebkitMask: glowMask, mask: glowMask,
              filter: 'blur(4px)',
            }} />
          )}
          {/* Colored arc */}
          {!empty && fillDeg > 0 && (
            <div style={{
              width: 200, height: 200, borderRadius: '50%',
              position: 'absolute', top: 0, left: 0,
              background: gradientBg,
              WebkitMask: ringMask, mask: ringMask,
            }} />
          )}
        </div>
        {/* Score text */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
          {empty ? (
            <>
              <p className="text-gray-300 text-sm font-medium">No score yet</p>
              <p className="text-gray-300 text-xs">Your score here</p>
            </>
          ) : (
            <>
              <p className="text-4xl font-bold text-gray-800" style={{ fontFamily: "'DM Serif Display', serif" }}>{score}</p>
              <p className="text-xs text-gray-400">out of 900</p>
            </>
          )}
        </div>
      </div>
    );
  };

  // History chart - SVG line+fill only, HTML for dots and labels
  const renderHistoryChart = () => {
    if (sorted.length < 2) return null;
    const last5 = sorted.slice(-5);
    const scores = last5.map(e => e.score);
    const minScore = Math.min(...scores) - 15;
    const maxScore = Math.max(...scores) + 15;
    const range = maxScore - minScore || 1;
    
    const lineColor = accent;
    const fillColor = plan ? 'rgba(185,166,240,0.18)' : isPink ? 'rgba(110,85,168,0.08)' : isWarm ? 'rgba(124,92,58,0.06)' : 'rgba(67,56,202,0.08)';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    
    const chartH = 110;
    const padX = 24; // px padding on sides for labels
    
    const chartPoints = last5.map((e, i) => {
      const xPct = last5.length === 1 ? 50 : (i / (last5.length - 1)) * 100;
      const yPct = ((e.score - minScore) / range) * 100;
      const [, m] = e.month_key.split('-').map(Number);
      return { xPct, yPct, score: e.score, monthLabel: months[m - 1] };
    });
    
    // SVG coordinates mapped to the chart area
    const svgW = 100;
    const svgH = 100;
    const svgPts = chartPoints.map(p => ({
      x: (p.xPct / 100) * svgW,
      y: svgH - (p.yPct / 100) * svgH
    }));
    const linePath = svgPts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    const fillPath = `${linePath} L ${svgPts[svgPts.length - 1].x} ${svgH} L ${svgPts[0].x} ${svgH} Z`;
    
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
        {/* Chart container */}
        <div style={{ position: 'relative', height: chartH, marginLeft: padX, marginRight: padX, marginTop: 22 }}>
          {/* SVG for line + fill — uses viewBox matching percentages */}
          <svg
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}
            viewBox={`0 0 ${svgW} ${svgH}`}
            preserveAspectRatio="none"
          >
            <path d={fillPath} fill={fillColor} />
            <path d={linePath} fill="none" stroke={lineColor} strokeWidth="2.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
          </svg>
          
          {/* HTML dots and score labels — positioned using percentages */}
          {chartPoints.map((p, i) => {
            const isLast = i === chartPoints.length - 1;
            const topPct = 100 - p.yPct;
            return (
              <div key={i} style={{
                position: 'absolute',
                left: `${p.xPct}%`,
                top: `${topPct}%`,
                transform: 'translate(-50%, -50%)',
                zIndex: 2,
              }}>
                {/* Score label */}
                <div style={{
                  position: 'absolute',
                  bottom: 14,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: 12,
                  fontWeight: isLast ? 800 : 600,
                  color: isLast ? lineColor : '#374151',
                  whiteSpace: 'nowrap',
                  fontFamily: "'DM Sans', sans-serif",
                  lineHeight: 1,
                }}>
                  {p.score}
                </div>
                {/* Dot */}
                <div style={{
                  width: isLast ? 12 : 10,
                  height: isLast ? 12 : 10,
                  borderRadius: '50%',
                  backgroundColor: isLast ? lineColor : 'white',
                  border: `2.5px solid ${lineColor}`,
                  boxSizing: 'border-box',
                }} />
              </div>
            );
          })}
        </div>
        
        {/* Month labels - fixed row below */}
        <div style={{ position: 'relative', height: 18, marginLeft: padX, marginRight: padX, marginTop: 6 }}>
          {chartPoints.map((p, i) => (
            <span key={i} style={{
              position: 'absolute',
              left: `${p.xPct}%`,
              transform: 'translateX(-50%)',
              fontSize: 11,
              fontWeight: 500,
              color: '#9ca3af',
            }}>
              {p.monthLabel}
            </span>
          ))}
        </div>
        
        {/* Summary */}
        {totalChange !== null && (
          <div className="flex items-center justify-between mt-3 px-0">
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center ${totalChange >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                <TrendingUp className={`w-3.5 h-3.5 ${totalChange >= 0 ? 'text-emerald-500' : 'text-red-500 rotate-180'}`} />
              </div>
              <div>
                <p className="text-xs font-bold" style={{ color: totalChange >= 0 ? '#10b981' : '#ef4444' }}>
                  {totalChange >= 0 ? '+' : ''}{totalChange} pts in {sorted.length} months
                </p>
                <p className="text-[10px] text-gray-400">
                  {totalChange > 10 ? 'Steady improvement' : totalChange > 0 ? 'Slight improvement' : totalChange === 0 ? 'No change' : 'Needs attention'}
                </p>
              </div>
            </div>
            <span className="text-xs text-gray-500 font-medium">
              {goal - latestScore > 0 ? `${goal - latestScore} to goal` : 'Goal reached!'}
            </span>
          </div>
        )}
        
        {/* View All History button */}
        <button
          onClick={() => {
            setShowAllHistory(true);
            const sheet = document.querySelector('[data-credit-sheet]');
            if (sheet) sheet.scrollTop = 0;
          }}
          className="w-full mt-3 py-2 text-xs font-semibold rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
          data-testid="view-all-history-btn"
        >
          View All History
        </button>
      </div>
    );
  };

  // Generate month options (last 12 months)
  const getMonthOptions = () => {
    const options = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
      options.push({ key, label });
    }
    return options;
  };

  const handleLogScore = () => {
    const score = parseInt(scoreInput);
    if (!score || score < 300 || score > 900) {
      customToast.error('Enter a score between 300\u2013900');
      return;
    }
    const monthKey = selectedMonth || currentMonthKey;
    const newEntry = { id: `cs_${Date.now()}`, score, month_key: monthKey, logged_at: new Date().toISOString() };
    const newEntries = [...entries.filter(e => e.month_key !== monthKey), newEntry];
    saveUserData({ credit_score: { ...creditScore, entries: newEntries, goal } });
    setScoreInput('');
    setSelectedMonth('');
    setShowLogDialog(false);
  };

  const handleSaveGoal = () => {
    const newGoal = parseInt(goalInput);
    if (!newGoal || newGoal < 300 || newGoal > 900) {
      customToast.error('Enter a goal between 300\u2013900');
      return;
    }
    saveUserData({ credit_score: { ...creditScore, entries, goal: newGoal } });
    setShowGoalEdit(false);
  };

  const goalProgress = hasEntries ? Math.min(100, Math.round(((latestScore - 300) / (goal - 300)) * 100)) : 0;
  const pointsNeeded = Math.max(0, goal - latestScore);

  const getMonthsToGoal = () => {
    if (sorted.length < 2 || pointsNeeded <= 0) return null;
    const avgChange = (latestScore - firstEntry.score) / (sorted.length - 1);
    if (avgChange <= 0) return null;
    return Math.ceil(pointsNeeded / avgChange);
  };

  const freeCheckSources = ['Borrowell', 'Credit Karma', 'Scotiabank app', 'TD MySpend', 'Equifax.ca'];

  // Score bands data
  const scoreBands = plan ? [
    { range: '300\u2013559', label: 'Poor', color: '#F2AFC8', desc: 'Needs work' },
    { range: '560\u2013659', label: 'Fair', color: '#F3DE8E', desc: 'Getting there' },
    { range: '660\u2013724', label: 'Good', color: '#A9C6F0', desc: 'Most lenders approve' },
    { range: '725\u2013759', label: 'Very Good', color: '#9ED9B6', desc: 'Better rates' },
    { range: '760\u2013900', label: 'Excellent', color: '#6FC394', desc: 'Best rates' },
  ] : [
    { range: '300\u2013559', label: 'Poor', color: '#ef4444', desc: 'Needs work' },
    { range: '560\u2013659', label: 'Fair', color: '#f59e0b', desc: 'Getting there' },
    { range: '660\u2013724', label: 'Good', color: '#22c55e', desc: 'Most lenders approve' },
    { range: '725\u2013759', label: 'Very Good', color: '#10b981', desc: 'Better rates' },
    { range: '760\u2013900', label: 'Excellent', color: '#047857', desc: 'Best rates' },
  ];

  // 5 credit factors data
  const creditFactors = [
    { icon: '\uD83D\uDCB3', title: 'Payment History', pct: 35, desc: 'Paying bills on time is the single biggest factor. Even one missed payment can drop your score significantly.' },
    { icon: '\uD83D\uDCCA', title: 'Credit Utilisation', pct: 30, desc: 'Keep your credit card balances below 30% of your limit \u2014 ideally under 10% for the best impact.' },
    { icon: '\uD83D\uDCC5', title: 'Credit History Length', pct: 15, desc: 'Older accounts help your score. Avoid closing old cards even if you don\u2019t use them.' },
    { icon: '\uD83D\uDD0D', title: 'New Credit & Inquiries', pct: 10, desc: 'Each application triggers a hard inquiry which can lower your score by 5\u201310 pts temporarily.' },
    { icon: '\uD83C\uDFE6', title: 'Credit Mix', pct: 10, desc: 'Having a variety of credit types shows lenders you can manage different products responsibly.' },
  ];

  const handleClose = () => {
    setShowLearnView(false);
    setShowAllHistory(false);
    setShowLogDialog(false);
    setShowGoalEdit(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Main Sheet */}
      <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 backdrop-blur-sm" onClick={handleClose}>
        <div 
          className="w-full max-w-lg bg-gray-50 rounded-t-3xl max-h-[90vh] overflow-y-auto overscroll-contain animate-in slide-in-from-bottom duration-300"
          style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}
          data-credit-sheet
          onClick={e => e.stopPropagation()}
        >
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>
          
          {/* Header */}
          <div className="flex items-center justify-between px-5 pb-3">
            {(showLearnView || showAllHistory) && (
              <button 
                onClick={() => { setShowLearnView(false); setShowAllHistory(false); }}
                className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-300 mr-2"
                data-testid="subview-back-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <h2 className="text-xl font-bold text-gray-800 flex-1" style={{ fontFamily: "'DM Serif Display', serif" }}>
              {showLearnView ? 'How Credit Scores Work' : showAllHistory ? 'Score History' : 'Credit Score'}
            </h2>
            <button 
              onClick={handleClose} 
              className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-300"
              data-testid="credit-sheet-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="px-5" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 96px)' }}>
            {/* === LEARN VIEW === */}
            {showLearnView && (
              <>
                <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                  Your credit score is a number between 300–900 that tells lenders how reliable you are with credit. The higher the better — here's what drives it.
                </p>
                
                {/* Score Bands */}
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Score Bands</p>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
                  {scoreBands.map((band, i) => (
                    <div key={band.label} className={`flex items-center px-4 py-3 ${i < scoreBands.length - 1 ? 'border-b border-gray-50' : ''}`}>
                      <div className="w-3 h-3 rounded-full flex-shrink-0 mr-3" style={{ backgroundColor: band.color }} />
                      <span className="text-sm font-bold text-gray-800 w-24">{band.range}</span>
                      <span className="text-sm font-bold flex-1" style={{ color: plan ? '#26252E' : band.color }}>{band.label}</span>
                      <span className="text-xs text-gray-400 text-right">{band.desc}</span>
                    </div>
                  ))}
                </div>
                
                {/* The 5 Factors */}
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">The 5 Factors</p>
                <div className="space-y-3 mb-4">
                  {creditFactors.map(factor => (
                    <div key={factor.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg ${isPink ? 'bg-pink-50' : isWarm ? 'bg-[#F5EDE4]' : 'bg-indigo-50'}`}>
                            {factor.icon}
                          </div>
                          <span className="text-sm font-bold text-gray-800" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                            {factor.title}
                          </span>
                        </div>
                        <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">{factor.pct}%</span>
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed mb-3">{factor.desc}</p>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${factor.pct}%`, background: bar }} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            
            {/* === ALL HISTORY VIEW === */}
            {showAllHistory && (
              <div className="space-y-2">
                {[...sorted].reverse().map((entry, i) => {
                  const [y, m] = entry.month_key.split('-').map(Number);
                  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                  const r = getRating(entry.score);
                  const prevEntry = sorted[sorted.indexOf(entry) - 1];
                  const diff = prevEntry ? entry.score - prevEntry.score : null;
                  return (
                    <div key={entry.id} className="bg-white rounded-2xl border border-gray-100 p-3 shadow-sm flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold text-gray-800">{monthNames[m - 1]} {y}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${r.color}20`, color: r.color }}>
                            {r.label}
                          </span>
                          {diff !== null && diff !== 0 && (
                            <span className="text-[10px] font-bold" style={{ color: diff > 0 ? '#10b981' : '#ef4444' }}>
                              {diff > 0 ? '+' : ''}{diff} pts
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-2xl font-bold" style={{ color: r.color, fontFamily: "'DM Serif Display', serif" }}>
                        {entry.score}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* === EMPTY STATE === */}
            {!showLearnView && !showAllHistory && !hasEntries && (
              <>
                {renderGauge(0, true)}
                
                <p className="text-center text-gray-400 text-sm mt-2 mb-6 px-4">
                  Log your first credit score to start tracking your financial health over time
                </p>
                
                <button
                  data-testid="log-first-score-btn"
                  onClick={() => setShowLogDialog(true)}
                  className="w-full py-3.5 rounded-2xl font-bold text-sm mb-6"
                  style={softBtn}
                >
                  Log My First Score
                </button>
                
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3 text-center">
                  What You'll Unlock
                </p>
                <div className="space-y-2 mb-6">
                  {[
                    { icon: '\uD83D\uDCC8', label: 'Score History Chart' },
                    { icon: '\uD83C\uDFAF', label: 'Goal Tracker' },
                    { icon: '\uD83D\uDCDA', label: 'How Credit Scores Work' },
                  ].map(item => (
                    <div key={item.label} className="bg-white rounded-2xl p-4 flex items-center justify-between border border-gray-100 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg ${isPink ? 'bg-pink-50' : isWarm ? 'bg-[#F5EDE4]' : 'bg-indigo-50'}`}>
                          {item.icon}
                        </div>
                        <span className="text-sm font-semibold text-gray-800">{item.label}</span>
                      </div>
                      <Lock className="w-4 h-4 text-gray-300" />
                    </div>
                  ))}
                </div>
                
                <div className="mb-2">
                  <p className="text-xs font-semibold text-gray-600 mb-2">Where to check your score for free</p>
                  <div className="flex flex-wrap gap-2">
                    {freeCheckSources.map(s => (
                      <span key={s} className="px-3 py-1.5 bg-white rounded-full text-xs text-gray-600 border border-gray-200 font-medium">{s}</span>
                    ))}
                  </div>
                </div>
              </>
            )}
            
            {/* === FILLED STATE === */}
            {!showLearnView && !showAllHistory && hasEntries && (
              <>
                {renderGauge(latestScore)}
                
                <div className="text-center mt-1">
                  <span className="text-xs font-bold px-3 py-1 rounded-full inline-block"
                    style={{ backgroundColor: `${rating.color}20`, color: rating.color }}>
                    {rating.label}
                  </span>
                </div>
                
                {change !== null && change !== 0 && (
                  <p className="text-center mt-2">
                    <span className="text-sm font-bold" style={{ color: change > 0 ? '#10b981' : '#ef4444' }}>
                      {change > 0 ? '\u2191' : '\u2193'} {change > 0 ? '+' : ''}{change} points
                    </span>
                    <span className="text-sm text-gray-400"> since {getPrevMonthName()}</span>
                  </p>
                )}
                
                <button
                  data-testid="log-monthly-score-btn"
                  onClick={() => setShowLogDialog(true)}
                  className="w-full py-3.5 rounded-2xl font-bold text-sm mt-4 mb-6"
                  style={softBtn}
                >
                  📝 Log Your Score
                </button>
                
                {sorted.length >= 2 && (
                  <div className="mb-5">
                    <p className="text-[13px] font-bold uppercase tracking-[0.08em] mb-3" style={{ color: '#7B5EA7' }}><span className="wy-star">✦ </span>Score history</p>
                    {renderHistoryChart()}
                  </div>
                )}
                
                {/* Score Goal */}
                <div className="mb-5">
                  <p className="text-[13px] font-bold uppercase tracking-[0.08em] mb-3" style={{ color: '#7B5EA7' }}><span className="wy-star">✦ </span>Score goal</p>
                  <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg ${isPink ? 'bg-pink-50' : isWarm ? 'bg-[#F5EDE4]' : 'bg-indigo-50'}`}>
                          🎯
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-800">Target: {goal}</p>
                          <p className="text-[10px] text-gray-400">
                            {pointsNeeded <= 0 ? 'Goal reached!' : getMonthsToGoal() ? `~${getMonthsToGoal()} months at current rate` : 'Keep going!'}
                          </p>
                        </div>
                      </div>
                      <span className="text-lg font-bold" style={{ color: accent }}>{Math.min(100, goalProgress)}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
                      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, goalProgress)}%`, background: bar }} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Current <span className="font-bold text-gray-700">{latestScore}</span></span>
                      <span className="text-xs text-gray-500">Needed <span className="font-bold text-gray-700">+{pointsNeeded} pts</span></span>
                      <button onClick={() => { setGoalInput(goal.toString()); setShowGoalEdit(true); }}
                        className="text-xs font-bold flex items-center gap-0.5" style={{ color: accent }}
                        data-testid="edit-goal-btn">
                        Edit <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Learn */}
                <div className="mb-4">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Learn</p>
                  <button 
                    onClick={() => {
                      setShowLearnView(true);
                      // Scroll sheet to top
                      const sheet = document.querySelector('[data-credit-sheet]');
                      if (sheet) sheet.scrollTop = 0;
                    }}
                    className="w-full bg-white rounded-2xl border border-gray-100 p-4 shadow-sm flex items-center justify-between text-left"
                    data-testid="how-scores-work-btn"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg ${isPink ? 'bg-pink-50' : isWarm ? 'bg-[#F5EDE4]' : 'bg-indigo-50'}`}>
                        📚
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">How Credit Scores Work</p>
                        <p className="text-[10px] text-gray-400">5 factors that affect your score</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </button>
                </div>
                
                <div className="mb-2">
                  <p className="text-xs font-semibold text-gray-600 mb-2">Where to check your score for free</p>
                  <div className="flex flex-wrap gap-2">
                    {freeCheckSources.map(s => (
                      <span key={s} className="px-3 py-1.5 bg-white rounded-full text-xs text-gray-600 border border-gray-200 font-medium">{s}</span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Log Score Overlay - rendered ABOVE the sheet at z-[200] */}
      {showLogDialog && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50" onClick={() => setShowLogDialog(false)}>
          <div className="bg-white rounded-3xl w-full max-w-[280px] mx-4 overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="px-4 pt-4 pb-2 text-center">
              <h3 className="text-base font-bold text-gray-800" style={{ fontFamily: "'DM Serif Display', serif" }}>
                Log Credit Score
              </h3>
            </div>
            {/* Month pills - scrollable */}
            <div className="px-4 pb-2">
              <p className="text-[10px] text-gray-400 font-medium mb-1.5">Select month</p>
              <div className="flex flex-wrap gap-1.5 max-h-[120px] overflow-y-auto">
                {getMonthOptions().map(opt => {
                  const isSelected = (selectedMonth || currentMonthKey) === opt.key;
                  const hasExisting = entries.some(e => e.month_key === opt.key);
                  return (
                    <button
                      key={opt.key}
                      onClick={() => setSelectedMonth(opt.key)}
                      data-testid={`month-${opt.key}`}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all ${
                        isSelected
                          ? 'font-bold'
                          : hasExisting 
                            ? 'bg-gray-100 text-gray-500'
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                      style={isSelected ? softBtn : undefined}
                    >
                      {opt.label.replace(` ${now.getFullYear()}`, '').replace(` ${now.getFullYear() - 1}`, ` '${String(now.getFullYear() - 1).slice(-2)}`)}
                      {hasExisting && ' ✓'}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Score input */}
            <div className="px-4 pb-1">
              <input
                type="number"
                min="300"
                max="900"
                value={scoreInput}
                onChange={(e) => setScoreInput(e.target.value)}
                placeholder="Score (300–900)"
                className="w-full h-12 text-center text-xl font-bold border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-0"
                style={{ '--tw-ring-color': accent }}
                data-testid="score-input"
                autoFocus
              />
            </div>
            <div className="flex gap-2 p-4 pt-2">
              <button onClick={() => setShowLogDialog(false)} className="flex-1 h-9 rounded-xl border border-gray-200 bg-white text-gray-700 font-semibold text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleLogScore}
                className="flex-1 h-9 rounded-xl font-bold text-sm"
                style={softBtn}
                data-testid="save-score-btn"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Goal Overlay - rendered ABOVE the sheet at z-[200] */}
      {showGoalEdit && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50" onClick={() => setShowGoalEdit(false)}>
          <div className="bg-white rounded-3xl w-full max-w-xs mx-4 overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-5 text-center">
              <h3 className="text-lg font-bold text-gray-800" style={{ fontFamily: "'DM Serif Display', serif" }}>
                Set Score Goal
              </h3>
              <p className="text-xs text-gray-400 mt-1">What score are you aiming for?</p>
            </div>
            <div className="px-5 pb-2">
              <input
                type="number"
                min="300"
                max="900"
                value={goalInput}
                onChange={(e) => setGoalInput(e.target.value)}
                placeholder="Target score (300–900)"
                className="w-full h-14 text-center text-2xl font-bold border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-0"
                style={{ '--tw-ring-color': accent }}
                data-testid="goal-input"
                autoFocus
              />
            </div>
            <div className="flex gap-2 p-5 pt-3">
              <button onClick={() => setShowGoalEdit(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 font-semibold text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleSaveGoal}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm"
                style={softBtn}
                data-testid="save-goal-btn"
              >
                Save Goal
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreditScoreSheet;
